// mixins/product-card-tracking.js
export default {
  data () {
    return {
      observer: null,
      trackedVisibility: null,
      cocoCards: [],
      cocoGroups: [],
      otherPartners: [],
      placementMapping: {
        autocomparison: 'comparison_table',
        deals: 'deals_widget',
        single: 'shopping_widget',
        double: 'shopping_widget_double',
        triple: 'shopping_widget_triple',
        slider: 'shopping_widget_slider',
        affiliateLink: 'affiliate_link'
      }
    }
  },
  computed: {
    // Check if product card tracking should be enabled based on metaData settings
    enabledProductCardTracking () {
      return this.metaData?.affiliateContent || this.metaData?.cocoContent || this.metaData?.showAffiliateDisclaimer
    }
  },
  mounted () {
    if (this.enabledProductCardTracking) {
      // Ensure the observer is set up only if tracking is enabled
      this.trackedVisibility = new Set()
      this.setupTracking()
    }
  },
  beforeUnmount () {
    if (this.observer) {
      this.observer.disconnect()
    }

    this.cocoCards.forEach((cocoCard) => {
      cocoCard.removeEventListener('click', this.handleCocoCardClick)
    })

    this.otherPartners.forEach((otherPartner) => {
      otherPartner.removeEventListener('click', this.handleOtherPartnerClick)
    })
  },
  methods: {
    /**
     * while we not have unique tracking IDs to identify products,
     * we need to extract the product ID from the URL or use a default ID
     * the default ID is a generated hash value based on the product's url
     *
     * @param {string} url - The URL to extract the product ID from
     * @param {string} defaultId - The default ID to return if no ID can be extracted
     * @returns {string} - The extracted product ID or the default ID
     */
    extractProductId (url, defaultId) {
      try {
        const parsedUrl = new URL(url)

        // 1. Amazon-URL with ASIN i.e. https://www.amazon.de/dp/B0DB8D5VGT
        const amazonAsinMatch = parsedUrl.pathname.match(/\/dp\/([A-Z0-9]{10})/)
        if (amazonAsinMatch) {
          return amazonAsinMatch[1] // → "B0DB8D5VGT"
        }

        // 2. Awin-URL with awinmid i.e. https://www.awin1.com/cread.php?awinmid=10092
        if (parsedUrl.hostname.includes('awin1.com')) {
          const awinmid = parsedUrl.searchParams.get('awinmid')
          if (awinmid) {
            return awinmid // → "10092"
          }
        }

        // 3. no matching pattern >> return defaultId
        return defaultId
      } catch (err) {
        // wrong URL format or other error >> return defaultId
        return defaultId
      }
    },
    setupTracking () {
      this.cocoCards = Array.from(document.querySelectorAll('.bx-product, [data-ga-coco-group-type="affiliateLink"]'))
      // if no product cards found, do not set up observer nor add event listeners
      if (!this.cocoCards.length) return

      // find all product groups and affiliate links to set up tracking information regarding their position
      // we need to track the position of each coco group within the page, so lets find all product groups and affiliate links
      this.cocoGroups = Array.from(document.querySelectorAll('.bx-product-group, .bx-affiliate-link'))
      this.cocoGroups.forEach((cocoGroup, index) => {
        // set position data attribute for tracking to each coco group
        cocoGroup.dataset.gaCocoPosition = `${index + 1}/${this.cocoGroups.length}`
      })

      // Initialize the IntersectionObserver to track visibility of product cards
      this.observer = new IntersectionObserver(this.visibleObserverCallback, {
        root: null,
        rootMargin: '0px',
        threshold: 0.98 // Trigger when 100% of the target is visible should be 1.00
        // but we reduce this to 0.98 to catch edge cases within slider,
        // the buttons reduce visibility of product cards
      })

      // Iterate over each product card to set up tracking
      // and add click event listeners
      this.cocoCards.forEach((cocoCard, index) => {
        // add product to observer for visibility tracking
        this.observer.observe(cocoCard)
        // add click event listener for click tracking to product card
        cocoCard.addEventListener('click', this.handleCocoCardClick)
      })

      // find all other partner links within the product cards
      this.otherPartners = Array.from(document.querySelectorAll('.bx-product__other-partners a'))
      // and for them we also need to set up a different click tracking
      this.otherPartners.forEach((otherPartner) => {
        // add click event listener for click tracking to other partner links aka affiliate links
        otherPartner.addEventListener('click', this.handleOtherPartnerClick)
      })
    },
    handleCocoCardClick (event) {
      // while the click event can have a target that is not a link, we want to track the closest link with class bx-product or just a link in case of affiliate links
      const closestLink = event.target.closest('.bx-product') || event.target.closest('a')
      // and now lets gather the data we need for tracking
      this.pushTrackingData(closestLink, 'click')
    },
    handleOtherPartnerClick (event) {
      // we have a click, lets find the closest other partners wrapper to find the products unique ID to which this affiliate link belongs
      const closestWrapper = event.target.closest('.bx-product__other-partners')
      const productUniqueId = closestWrapper.dataset.productUniqueId

      // find the related product card by its unique ID starting from the closest product group
      // this is needed because the product card can be duplicated for comparison table
      // where we have multiple product cards within the same product group
      // and we need to track the click on the affiliate link for the correct product card
      const closestProductGroup = event.target.closest('.bx-product-group')
      const relatedProduct = closestProductGroup.querySelector('a[data-ga-coco-unique-id="' + productUniqueId + '"]')

      // and now lets gather additional data for tracking from the affiliate link
      const closestLink = event.target.closest('a')
      const additionalData = {
        merchant: closestLink.querySelector('span')?.textContent || '',
        // we increment the position by 1 while first position is always the product card link
        affiliate_link_position: Number(closestLink.dataset?.gaCocoPartnerPosition) + 1 || '',
        product_url: closestLink.href
      }

      // and now we collect the data of the corresponding product card and enrich it with our additional data
      this.pushTrackingData(relatedProduct, 'click', additionalData)
    },
    visibleObserverCallback (entries, observer) {
      entries.forEach(entry => {
        // although we have unique IDs for each product card, we need to add an extra identifier while products are duplicated for comparison table
        const uniqueProductInGroupId = entry.target.dataset.gaCocoUniqueId + '-' + entry.target.dataset.gaCocoGroupType
        // Check if the product card is intersecting and not already tracked
        if (entry.isIntersecting && !this.trackedVisibility.has(uniqueProductInGroupId)) {
          // If the product card is visible and not tracked, track it
          this.trackedVisibility.add(uniqueProductInGroupId)
          // and now lets gather the data we need for tracking
          this.pushTrackingData(entry.target, 'visibility')
        }
      })
    },
    pushTrackingData (target, type, additionalData = {}) {
      const groupType = target.dataset.gaCocoGroupType
      // we have do diffentitate between product cards and affiliate links
      const isAffiliateLink = groupType === 'affiliateLink'
      // get the bounding rectangle of the target element to calculate its position
      const entryBound = target.getBoundingClientRect()
      // calculate position where the product card starts relative to the viewport
      const entryTopDistance = entryBound.top + window.scrollY
      // get the bounding rectangle of the article container to calculate its length
      const articleContainerBound = document.querySelector('.bx-aside-wrapper > div > .bx-paragraph-list').getBoundingClientRect()
      // calculate position where paragraphs container ends aka end of the article
      const articleBottomDistance = articleContainerBound.height + articleContainerBound.top + window.scrollY
      // and finally we can calculate the position of the product card relative to the end of the article container
      const relativePosition = Math.round(100 / articleBottomDistance * entryTopDistance) + '%'
      // we need to set different event values based on the type of tracking
      const event = type === 'visibility' ? 'product_card_impression' : 'product_card_click'
      // and we need to determine the placement based on the product group type
      const placement = this.placementMapping[groupType] || 'unknown_placement'
      // and we need to extract the merchant name from the product card roofline if available
      const merchant = target.querySelector('.bx-product__roofline')?.textContent || ''

      const productCardPosition = target.closest(isAffiliateLink ? '.bx-affiliate-link' : '.bx-product-group').dataset.gaCocoPosition || 'unknown_position'

      // Prepare the data object to be pushed to the dataLayer
      const data = {
        event,
        product_card_id: this.extractProductId(target.href, target.dataset.gaCocoUniqueId),
        placement,
        merchant,
        affiliate_link_position: 1, // Default value, can be overridden by additionalData
        product_card_position: productCardPosition,
        product_card_rel_position: relativePosition,
        product_url: target.href,
        ...additionalData
      }

      // affiliateLinks do not have merchant information, so we remove it from the data object
      if (isAffiliateLink) {
        delete data.merchant
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(data)
    }
  }
}
